import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'

import React from 'react'
import { Autoplay, Navigation, Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

import { CustomImage } from '@/components/CustomImage/CustomImage'
import { CarouselBlock } from '@/slices/blog/mocks/articles'
import { pxToRem } from '@/shared/styles/themes'
import { Box, styled, Typography } from '@mui/material'

export interface ArticleCarouselProps {
  carousel: CarouselBlock
}

export const ArticleCarousel: React.FC<ArticleCarouselProps> = ({ carousel }) => {
  const { images, autoplay = false, loop = true } = carousel

  if (!images || images.length === 0) {
    return null
  }

  return (
    <CarouselWrapper>
      <StyledSwiper
        modules={[Navigation, Pagination, Autoplay]}
        spaceBetween={20}
        slidesPerView={1}
        navigation
        pagination={{ clickable: true }}
        autoplay={autoplay ? { delay: 5000, disableOnInteraction: false } : false}
        loop={loop && images.length > 1}
        breakpoints={{
          640: {
            slidesPerView: 1,
            spaceBetween: 20,
          },
          768: {
            slidesPerView: images.length === 1 ? 1 : 1.2,
            spaceBetween: 24,
          },
          1024: {
            slidesPerView: images.length === 1 ? 1 : 1.5,
            spaceBetween: 32,
          },
        }}
      >
        {images.map((image, index) => (
          <SwiperSlide key={index}>
            <SlideContent>
              <ImageContainer>
                <CustomImage
                  src={image.src}
                  alt={image.alt}
                  variant="horizontal"
                  objectFit="cover"
                  borderRadius={12}
                />
              </ImageContainer>
              {image.caption && (
                <Caption variant="body2">
                  {image.caption}
                </Caption>
              )}
            </SlideContent>
          </SwiperSlide>
        ))}
      </StyledSwiper>
    </CarouselWrapper>
  )
}

const CarouselWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  margin: `${pxToRem(32)} 0`,
  [theme.breakpoints.down('md')]: {
    margin: `${pxToRem(24)} 0`,
  },
}))

const StyledSwiper = styled(Swiper)(({ theme }) => ({
  width: '100%',
  paddingBottom: pxToRem(48),

  '& .swiper-button-prev, & .swiper-button-next': {
    color: theme.palette.primary.main,
    width: pxToRem(44),
    height: pxToRem(44),
    backgroundColor: theme.palette.background.default,
    borderRadius: '50%',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.common.white,
      transform: 'scale(1.1)',
    },
    '&::after': {
      fontSize: pxToRem(18),
      fontWeight: 'bold',
    },
  },

  '& .swiper-button-prev': {
    left: pxToRem(10),
    [theme.breakpoints.down('sm')]: {
      left: pxToRem(5),
    },
  },

  '& .swiper-button-next': {
    right: pxToRem(10),
    [theme.breakpoints.down('sm')]: {
      right: pxToRem(5),
    },
  },

  '& .swiper-pagination': {
    bottom: pxToRem(10),
  },

  '& .swiper-pagination-bullet': {
    width: pxToRem(10),
    height: pxToRem(10),
    backgroundColor: theme.palette.text.disabled,
    opacity: 0.5,
    transition: 'all 0.3s ease',
  },

  '& .swiper-pagination-bullet-active': {
    backgroundColor: theme.palette.primary.main,
    opacity: 1,
    width: pxToRem(24),
    borderRadius: pxToRem(5),
  },

  [theme.breakpoints.down('sm')]: {
    paddingBottom: pxToRem(40),
    '& .swiper-button-prev, & .swiper-button-next': {
      width: pxToRem(36),
      height: pxToRem(36),
      '&::after': {
        fontSize: pxToRem(16),
      },
    },
  },
}))

const SlideContent = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  gap: pxToRem(12),
})

const ImageContainer = styled(Box)({
  width: '100%',
  borderRadius: pxToRem(12),
  overflow: 'hidden',
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)',
})

const Caption = styled(Typography)(({ theme }) => ({
  fontSize: pxToRem(14),
  color: theme.palette.text.secondary,
  fontStyle: 'italic',
  textAlign: 'center',
  paddingTop: pxToRem(8),
  [theme.breakpoints.down('sm')]: {
    fontSize: pxToRem(12),
  },
}))
